import type { CustomEvent as ObsrviqCustomEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { cssPath } from './serialize.js';
import { uuid } from './util.js';

/**
 * Form-field analytics. Watches every form control (input / textarea / select)
 * and, on each blur, emits a lightweight `form_field` custom event describing
 * the *interaction* — never the value:
 *
 *   { form, field, type, ms, changed, filled, corrected, kind }
 *
 * One event per focus→blur, carrying that focus's duration (`ms`). The server
 * sums `ms` across events for total time-on-field, counts events per session
 * for re-entries, and reads `filled` to find fields people leave blank.
 *
 * Two companion signals complete the picture:
 *   • `form_submit` — the form was submitted (`via` records how we knew).
 *   • `form_error`  — a field failed validation (`reason` is a fixed enum).
 *
 * Privacy: values are never read. `filled` is a boolean derived from value
 * length (or `checked`); the field key comes from structural attributes, and
 * every free-text hint is redacted before it leaves the page (see `redact`).
 * Controls inside a blocked region (the configured block selector) are skipped.
 *
 * ── Why this is not the obvious implementation ────────────────────────────
 * Measured against 187,625 real interactions from a production React app:
 *
 *   • 79.4% of field keys were React `useId()` values (`_r_12_`, `_r_bas_`).
 *     Those are assigned by render-tree position, so they are meaningless to a
 *     human AND change when the customer ships a UI tweak — 35% of (form,field)
 *     pairs were seen exactly once. Framework-generated ids are now rejected
 *     outright and naming falls through to the visible label, which was present
 *     on 93% of interactions.
 *   • 98.9% of interactions belonged to a `page:<path>` bucket, which by
 *     construction has no <form> and therefore could NEVER emit the native
 *     submit event — so 99.3% of forms reported a 0% submit rate. Submission is
 *     now also detected from a click on the scope's submit control.
 *   • `placeholder` was used as the field key. On the sampled site those were
 *     static examples, but `placeholder={value}` is a common edit-form pattern,
 *     so that path could put a real name or phone number in an aggregation key.
 *     Placeholders are now a last-resort hint only, never a key, and are
 *     dropped entirely for the types where values look like labels.
 */

const TRACKED = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
// Non-data inputs: buttons and hidden/file fields carry no fill behaviour worth
// measuring (and file is privacy-adjacent), so they're left out.
const SKIP_INPUT_TYPES = new Set(['hidden', 'submit', 'button', 'reset', 'image', 'file']);
const MAX_KEY = 80;
const MAX_HINT = 60; // naming hints are short labels, not keys
const MAX_FOCUS_MS = 300_000; // cap idle-inflated focus time (walked-away tab)
const MIN_FOCUS_MS = 100; // ignore sub-100ms programmatic focus blips

/**
 * Identifiers a framework generated, which are stable only until the next
 * render or the next deploy. Keying aggregates on these silently fragments a
 * field's history, so they are treated as if the attribute were absent.
 * React 18 (`:r0:`, `«r0»`), React 19 (`_r_1a_`), MUI, Radix, Headless UI,
 * Downshift, react-select, Ember, plus bare numeric and uuid/hex ids.
 */
const UNSTABLE_ID =
  /^(_r_[0-9a-z]+_|:r[0-9a-z]+:|«r[0-9a-z]+»|:[a-z0-9]{1,6}:|mui-\d+|radix-[:a-z0-9]+|headlessui-[\w-]*\d+|downshift-\d+[\w-]*|react-select-\d+[\w-]*|ember\d+|\d+|[0-9a-f]{8}-[0-9a-f-]{27,}|[0-9a-f]{16,})$/i;

/** A search / filter box is not a form field — it has no completion to measure. */
const SEARCHY = /(^|[_\-.[])(search|filter|query|lookup|autocomplete)([_\-.\]]|$)|^q$/i;

/**
 * Free text leaving the page (a label, a validation message) is redacted first.
 * These hints are UI chrome by intent, but chrome is templated — "Welcome back,
 * Jane" and "Must be under ₹50,000" both reach us through the same code path —
 * so anything value-shaped is masked rather than trusted.
 */
function redact(s: string): string {
  return s
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '▒')
    .replace(/[+]?\d[\d ()-]{6,}\d/g, '▒')
    .replace(/\d{4,}/g, '▒');
}

/** Does this string look like a value rather than a label? */
function valueLike(s: string): boolean {
  return /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/.test(s) || /^[+]?[\d ()-]{6,}$/.test(s) || /^\d+$/.test(s);
}

type FormControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function isTracked(el: EventTarget | null): el is FormControl {
  if (!(el instanceof HTMLElement) || !TRACKED.has(el.tagName)) return false;
  if (el instanceof HTMLInputElement && SKIP_INPUT_TYPES.has(el.type)) return false;
  return true;
}

function controlType(el: FormControl): string {
  if (el instanceof HTMLTextAreaElement) return 'textarea';
  if (el instanceof HTMLSelectElement) return 'select';
  return el.type || 'text';
}

/** Non-empty / checked — a boolean only, the value itself is never read. */
function isFilled(el: FormControl): boolean {
  if (el instanceof HTMLInputElement) {
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked;
    return el.value.trim().length > 0;
  }
  if (el instanceof HTMLSelectElement) return el.value !== '';
  return el.value.trim().length > 0;
}

function clip(s: string, max = MAX_KEY): string {
  const t = s.trim().replace(/\s+/g, ' ');
  return t.length > max ? t.slice(0, max) : t;
}

/** An attribute is usable as a key only if a framework didn't generate it. */
function stableAttr(el: Element, name: string): string | undefined {
  const v = el.getAttribute(name);
  if (!v) return undefined;
  const t = v.trim();
  if (!t || UNSTABLE_ID.test(t)) return undefined;
  return clip(t);
}

/** Visible label text for a control, from its <label> / aria wiring. */
function labelText(el: FormControl): string | undefined {
  const byAria = el.getAttribute('aria-labelledby');
  if (byAria) {
    const t = byAria
      .split(/\s+/)
      .map((id) => el.ownerDocument.getElementById(id)?.textContent || '')
      .join(' ')
      .trim();
    if (t) return clip(t);
  }
  if (el.labels && el.labels.length) {
    const t = el.labels[0]!.textContent?.trim();
    if (t) return clip(t);
  }
  return undefined;
}

/**
 * A stable, human-readable field identity from structural attributes only.
 *
 * Order is deliberate: developer-authored attributes first (they survive
 * re-renders and say what the field means), then the visible label, then the
 * semantic `autocomplete` token, and only then a positional selector. A
 * framework-generated id is skipped at every step rather than accepted, since
 * an opaque key that changes on deploy is worse than a structural one.
 */
function fieldKey(el: FormControl): string {
  const name = stableAttr(el, 'name');
  if (name) return name;
  const id = stableAttr(el, 'id');
  if (id) return id;
  for (const a of ['data-testid', 'data-test', 'data-qa', 'data-cy']) {
    const v = stableAttr(el, a);
    if (v) return v;
  }
  const aria = el.getAttribute('aria-label')?.trim();
  if (aria && !valueLike(aria)) return clip(redact(aria));
  const lbl = labelText(el);
  if (lbl && !valueLike(lbl)) return clip(redact(lbl));
  const ac = (el.getAttribute('autocomplete') || '').trim().toLowerCase();
  if (ac && ac !== 'off' && ac !== 'on') return clip(ac);
  // Nothing nameable — fall back to a positional selector, which is at least
  // stable for a static tree. Placeholder is deliberately NOT used here: it is
  // the one attribute customers routinely bind to the current value.
  //
  // cssPath short-circuits on an id, so a framework id rejected above would
  // walk straight back in as `#_r_u_`. Guard that specific case with an
  // ordinal instead: position among same-type controls in the same scope is
  // dull, but it survives a re-render, which is the whole point.
  const path = cssPath(el);
  if (path.startsWith('#') && UNSTABLE_ID.test(path.slice(1))) {
    const scope = scopeOf(el) || el.ownerDocument.body;
    const t = controlType(el);
    const peers = Array.from(scope.querySelectorAll<FormControl>('input,textarea,select')).filter(
      (c) => isTracked(c) && controlType(c) === t,
    );
    const i = peers.indexOf(el);
    return clip(i >= 0 ? `${t} #${i + 1}` : t);
  }
  return clip(path);
}

function clipHint(s: string | null | undefined): string | undefined {
  if (!s) return undefined;
  const t = s.trim().replace(/\s+/g, ' ');
  if (!t) return undefined;
  return clip(redact(t), MAX_HINT);
}

/**
 * Visible text a human reads as this field's label — used ONLY as a naming hint
 * for the server's AI pass, never as the stable key. It reads static UI chrome
 * (a <label>, a wrapper's own text, a preceding label element) so opaque keys
 * can still be named "Work email". The control's value is never read; tracked
 * controls and buttons are skipped so a sibling input's contents can't leak in.
 */
function nearbyText(el: FormControl): string | undefined {
  const lbl = labelText(el);
  if (lbl) return clipHint(lbl);
  const aria = clipHint(el.getAttribute('aria-label'));
  if (aria) return aria;
  // A label rendered as a plain element just before the control.
  let sib: Element | null = el.previousElementSibling;
  for (let hops = 0; sib && hops < 3; hops++, sib = sib.previousElementSibling) {
    if (TRACKED.has(sib.tagName) || /^(BUTTON|SCRIPT|STYLE|SVG)$/.test(sib.tagName)) continue;
    const t = clipHint(sib.textContent);
    if (t) return t;
  }
  // <div class="field">Label <input/></div> — the wrapper's own direct text.
  const parent = el.parentElement;
  if (parent) {
    const direct = clipHint(
      Array.from(parent.childNodes).filter((n) => n.nodeType === 3).map((n) => n.textContent || '').join(' '),
    );
    if (direct) return direct;
    const l2 = clipHint(parent.querySelector('label,legend')?.textContent);
    if (l2) return l2;
  }
  // Placeholder is the weakest hint and the one most likely to hold a value, so
  // it is used only when nothing else named the field, and never for the types
  // where a bound value would be indistinguishable from an example.
  const t = controlType(el);
  if (t === 'password' || t === 'email' || t === 'tel' || t === 'number') return undefined;
  const ph = el.getAttribute('placeholder');
  return ph && !valueLike(ph) ? clipHint(ph) : undefined;
}

/**
 * The nearest heading/legend a user sees over the owning form — a naming hint.
 *
 * Deliberately returns nothing when the form has no heading of its own. The
 * previous version fell back to `document.title`, which on a single-page app is
 * one constant string: every form on the sampled site came back labelled
 * "Bhoomee Store", which is worse than no label because it looks like an answer.
 */
function formTitleHint(el: Element): string | undefined {
  const scope = el.closest('form,fieldset,dialog,[role="form"],[role="dialog"],section,[role="group"]');
  if (!scope) return undefined;
  const heading = clipHint(scope.querySelector('legend,h1,h2,h3,h4,[role="heading"]')?.textContent);
  if (heading) return heading;
  return clipHint(scope.getAttribute('aria-label'));
}

/** Privacy-safe descriptive hints (label text, autocomplete intent, form heading)
 *  attached to each interaction so the server can name opaque fields/forms. Only
 *  present keys are emitted, keeping the wire small. */
function namingHints(el: FormControl): Record<string, string> {
  const out: Record<string, string> = {};
  const ac = (el.getAttribute('autocomplete') || '').trim().toLowerCase();
  if (ac && ac !== 'off' && ac !== 'on') out.autocomplete = ac.slice(0, MAX_HINT);
  const near = nearbyText(el);
  if (near) out.near = near;
  const ft = formTitleHint(el);
  if (ft) out.formTitle = ft;
  return out;
}

/**
 * The element that bounds one logical form.
 *
 * A real <form> is preferred, but SPAs routinely render a modal or a card with
 * no <form> at all — those were previously all collapsed into a single
 * `page:<path>` bucket per page, which is how one production page ended up
 * reporting 5,733 "fields". Falling back to the nearest dialog / fieldset /
 * labelled group keeps distinct forms on a page distinct.
 */
function scopeOf(el: Element): Element | null {
  const f = (el as FormControl).form as HTMLFormElement | null;
  if (f) return f;
  return el.closest('[data-form],[role="form"],dialog,[role="dialog"],fieldset,[role="group"]');
}

/** A stable identity for the owning form (or a per-page bucket for loose fields). */
function formKey(el: FormControl): string {
  const scope = scopeOf(el);
  if (scope) {
    const name = stableAttr(scope, 'name') || stableAttr(scope, 'data-form') || stableAttr(scope, 'id');
    if (name) return name;
    const aria = scope.getAttribute('aria-label')?.trim();
    if (aria && !valueLike(aria)) return clip(redact(aria));
    const action = scope.getAttribute('action');
    if (action) {
      try {
        return clip(new URL(action, location.href).pathname);
      } catch {
        return clip(action);
      }
    }
    const heading = scope.querySelector('legend,h1,h2,h3')?.textContent?.trim();
    if (heading && !valueLike(heading)) return clip(redact(heading));
    return clip(cssPath(scope));
  }
  // Form-less controls (common in SPAs) bucket by page path. The path is NOT
  // templated here — the server does that with the same cardinality detector
  // the rest of the product uses, so historical rows collapse too.
  return clip(`page:${location.pathname}`);
}

/** Buttons that mean "give up", which must never be read as a submission. */
const NEGATIVE_BTN = /\b(cancel|close|back|reset|clear|dismiss|delete|remove|discard|skip|logout|sign out)\b/i;
/** Buttons that mean "commit this form". */
const POSITIVE_BTN =
  /\b(submit|save|continue|next|create|update|send|confirm|apply|finish|done|register|sign ?up|sign ?in|log ?in|place order|checkout|pay|book|order|add|search)\b/i;

/**
 * Does clicking this element commit the surrounding form?
 *
 * `button[type=submit]` is definitive. Everything else is a judgement call, so
 * the text is required to read like a commit and required NOT to read like an
 * escape — a "Cancel" inside a <form> would otherwise be counted as a
 * submission and quietly inflate the completion rate.
 */
function submitIntent(el: Element): boolean {
  const btn = el.closest('button,input[type="submit"],input[type="button"],[role="button"],a[href="#"]');
  if (!btn) return false;
  const txt = (btn.textContent || btn.getAttribute('aria-label') || btn.getAttribute('value') || '').trim();
  if (NEGATIVE_BTN.test(txt)) return false;
  if (btn instanceof HTMLInputElement && btn.type === 'submit') return true;
  if (btn instanceof HTMLButtonElement && btn.type === 'submit') return true;
  return POSITIVE_BTN.test(txt);
}

/** Fixed vocabulary for why a field failed validation — never free text. */
function invalidReason(el: FormControl): string {
  const v = (el as HTMLInputElement).validity;
  if (!v) return 'invalid';
  if (v.valueMissing) return 'required';
  if (v.typeMismatch) return 'format';
  if (v.patternMismatch) return 'pattern';
  if (v.tooShort) return 'too_short';
  if (v.tooLong) return 'too_long';
  if (v.rangeUnderflow) return 'too_small';
  if (v.rangeOverflow) return 'too_large';
  if (v.stepMismatch) return 'step';
  if (v.badInput) return 'bad_input';
  if (v.customError) return 'custom';
  return 'invalid';
}

/** The app's own error text for a field, via aria-describedby / a sibling alert. */
function errorMessage(el: FormControl): string | undefined {
  const ids = el.getAttribute('aria-describedby') || el.getAttribute('aria-errormessage');
  if (ids) {
    const t = ids
      .split(/\s+/)
      .map((id) => el.ownerDocument.getElementById(id)?.textContent || '')
      .join(' ')
      .trim();
    if (t) return clipHint(t);
  }
  const alert = el.parentElement?.querySelector('[role="alert"],.error,.invalid-feedback,.field-error');
  return alert ? clipHint(alert.textContent) : undefined;
}

export function instrumentForms(ctx: InstrumentCtx): Teardown {
  const block = ctx.config.blockSelector;
  // Per-focus state for the currently-focused control.
  let focused: { el: FormControl; start: number; changed: boolean; wasFilled: boolean } | null = null;
  // Scopes that have seen a field interaction — a submit only counts for these,
  // so a stray button click on a page with no form activity is ignored.
  const touched = new Set<string>();
  // Fields already reported invalid for the current error pass, so a framework
  // re-rendering the same error three times doesn't triple-count it.
  const reported = new Set<string>();

  const emit = (name: string, props: Record<string, unknown>) => {
    const e: ObsrviqCustomEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'custom',
      t: ctx.clock.now(),
      ts: ctx.clock.wall(),
      name,
      props,
    };
    ctx.emit(e);
  };

  const blocked = (el: Element): boolean => {
    try {
      return !!block && !!el.closest(block);
    } catch {
      return false;
    }
  };

  const flush = () => {
    if (!focused) return;
    const { el, start, changed, wasFilled } = focused;
    focused = null;
    const ms = Math.min(MAX_FOCUS_MS, Math.max(0, ctx.clock.now() - start));
    // Drop pass-through blips that weren't real interactions.
    if (ms < MIN_FOCUS_MS && !changed) return;
    const form = formKey(el);
    const type = controlType(el);
    const name = el.getAttribute('name') || el.id || '';
    touched.add(form);
    emit('form_field', {
      form,
      field: fieldKey(el),
      type,
      ms,
      changed,
      filled: isFilled(el),
      // Editing a field that already had content is a correction, which reads
      // very differently from first-time entry: it means the value was wrong,
      // rejected, or misunderstood.
      corrected: changed && wasFilled,
      // Search and filter boxes have no completion to measure; tagging them
      // lets the report separate them instead of scoring them as form fields.
      ...(type === 'search' || SEARCHY.test(name) ? { kind: 'search' } : {}),
      ...namingHints(el),
    });
  };

  const onFocusIn = (ev: FocusEvent) => {
    const el = ev.target;
    if (!isTracked(el) || blocked(el)) return;
    if (focused && focused.el !== el) flush(); // safety: blur may have been missed
    focused = { el, start: ctx.clock.now(), changed: false, wasFilled: isFilled(el) };
  };

  const onInput = (ev: Event) => {
    if (focused && ev.target === focused.el) {
      focused.changed = true;
      // A fresh edit means any error we already reported can be reported again
      // if it comes back — that second failure is the interesting one.
      reported.delete(fieldKey(focused.el));
    }
  };

  const onFocusOut = (ev: FocusEvent) => {
    if (focused && ev.target === focused.el) flush();
  };

  /** Record one validation failure, deduped per field until the user edits it. */
  const reportError = (el: FormControl, reason: string) => {
    if (blocked(el)) return;
    const field = fieldKey(el);
    if (reported.has(field)) return;
    reported.add(field);
    const message = errorMessage(el);
    emit('form_error', {
      form: formKey(el),
      field,
      type: controlType(el),
      reason,
      ...(message ? { message } : {}),
    });
  };

  // Native constraint validation (required, type=email, pattern, min/max).
  const onInvalid = (ev: Event) => {
    const el = ev.target;
    if (isTracked(el)) reportError(el, invalidReason(el));
  };

  // Framework validation (react-hook-form, Formik, Angular) signals failure by
  // setting aria-invalid rather than firing `invalid`, so that attribute is
  // watched too. attributeFilter keeps this observer off every other mutation.
  const obs =
    typeof MutationObserver !== 'undefined'
      ? new MutationObserver((muts) => {
          for (const m of muts) {
            const el = m.target;
            if (!isTracked(el)) continue;
            if (el.getAttribute('aria-invalid') === 'true') reportError(el, 'rejected');
          }
        })
      : null;

  const onSubmit = (ev: Event) => {
    const form = ev.target;
    if (!(form instanceof HTMLFormElement) || blocked(form)) return;
    // Flush the in-progress field so its time lands before the submit marker.
    flush();
    // Reuse a tracked control to derive the key consistently; else fall back to the form itself.
    const probe = form.querySelector<FormControl>('input,textarea,select');
    const key = probe && !blocked(probe) ? formKey(probe) : clip(stableAttr(form, 'name') || stableAttr(form, 'id') || cssPath(form));
    touched.delete(key);
    emit('form_submit', { form: key, via: 'native' });
  };

  /**
   * Form-less submission. A React modal with no <form> never fires `submit`, so
   * a click on its commit button is the only observable moment of intent — and
   * on the sampled production site that path accounted for 98.9% of all form
   * activity. Gated on the scope having actually been filled in, so this cannot
   * manufacture submissions on pages with no form.
   */
  const onClick = (ev: MouseEvent) => {
    const t = ev.target;
    if (!(t instanceof Element) || blocked(t)) return;
    if (!submitIntent(t)) return;
    // A native <form> ancestor will fire `submit` itself — don't double-count.
    if (t.closest('form')) return;
    const scope = t.closest('[data-form],[role="form"],dialog,[role="dialog"],fieldset,[role="group"]');
    const probe = (scope || document).querySelector<FormControl>('input,textarea,select');
    const key = probe && !blocked(probe) ? formKey(probe) : clip(`page:${location.pathname}`);
    if (!touched.has(key)) return; // nothing was filled in — not a submission
    flush();
    touched.delete(key);
    emit('form_submit', { form: key, via: 'click' });
  };

  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('invalid', onInvalid, true);
  document.addEventListener('click', onClick, true);
  obs?.observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ['aria-invalid'] });
  // A navigation/unload can steal focus without a focusout — flush what we have.
  window.addEventListener('pagehide', flush, true);

  return () => {
    flush();
    obs?.disconnect();
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('input', onInput, true);
    document.removeEventListener('submit', onSubmit, true);
    document.removeEventListener('invalid', onInvalid, true);
    document.removeEventListener('click', onClick, true);
    window.removeEventListener('pagehide', flush, true);
  };
}
